import React, { forwardRef, CSSProperties, ReactNode } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Grid, GridProps, Typography, Box, CardHeaderProps, Button, Divider, Card } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBackIosOutlined';

// project-imports
import useConfig from 'hooks/useConfig';
import { useNavigate } from 'react-router-dom';

// types
import { KeyedObject } from 'types/root';

// header style
const headerSX = {
  p: 2.5,
  '& .MuiCardHeader-action': { m: '0px auto', alignSelf: 'center' }
};

// ==============================|| CUSTOM - MAIN GRID ||============================== //

export interface MainCardProps extends KeyedObject {
  border?: boolean;
  boxShadow?: boolean;
  subheader?: ReactNode | string;
  style?: CSSProperties;
  content?: boolean;
  contentSX?: CSSProperties;
  darkTitle?: boolean;
  divider?: boolean;
  sx?: GridProps['sx'];
  secondary?: CardHeaderProps['action'];
  shadow?: string;
  elevation?: number;
  title?: ReactNode | string;
  codeString?: string;
  modal?: boolean;
  backButton?: boolean;
  onBackButtonClick?: () => void;
}

const MainCard = forwardRef<HTMLDivElement, React.PropsWithChildren<MainCardProps>>(
  (
    {
      border = true,
      boxShadow = true,
      children,
      subheader,
      content = true,
      contentSX = {},
      darkTitle,
      divider = false,
      elevation,
      secondary,
      shadow,
      sx = {},
      title,
      codeString,
      modal = false,
      backButton = false,
      onBackButtonClick,
      ...others
    },
    ref
  ) => {
    const theme = useTheme();
    const { themeContrast } = useConfig();
    const navigate = useNavigate();

    const handleBackButtonClick = () => {
      if (onBackButtonClick) {
        onBackButtonClick();
      } else {
        navigate(-1);
      }
    };

    return (
      <Card
        component={Grid}
        container
        className="pageMainCard"
        display="initial"
        ref={ref}
        {...others}
        sx={{
          position: 'relative',
          border: border ? '1px solid' : 'none',
          borderRadius: 1.5,
          borderColor: theme.palette.divider,
          ...(((themeContrast && boxShadow) || shadow) && {
            boxShadow: shadow ? shadow : theme.customShadows.z1
          }),
          ...(modal && {
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: `calc( 100% - 50px)`, sm: 'auto' },
            '& .MuiGrid-root': {
              overflowY: 'auto',
              minHeight: 'auto',
              maxHeight: `calc(100vh - 200px)`
            }
          }),
          ...sx
        }}
      >
        {/* grid header and action */}
        {!darkTitle && (title || backButton) && (
          <Grid item xs={12} sx={headerSX} className="pageTitle">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">{title}</Typography>
              {backButton && (
                <Button
                  variant="outlined"
                  className="globalBackButton"
                  aria-label="Back"
                  onClick={handleBackButtonClick}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
              )}
              {secondary}
            </Box>
            {subheader && <Typography variant="subtitle2">{subheader}</Typography>}
          </Grid>
        )}
        {darkTitle && title && <></>}

        {divider && <Divider />}
        {/* grid content */}
        {content && (
          <Grid item xs={12} className="pageBody" sx={{ ...contentSX, alignItems: 'flex-start' }}>
            {children}
          </Grid>
        )}
        {!content && children}
      </Card>
    );
  }
);

export default MainCard;
